"use client";

import { c as _c } from "react/compiler-runtime";
import { AuthContext } from "@/components/auth/Auth";
import { useMyStream } from "@/contexts/wave/MyStreamContext";
import type { DropRateChangeRequest } from "@/entities/IDrop";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ApiDropContextProfileContext } from "@/generated/models/ApiDropContextProfileContext";
import { DropSize } from "@/helpers/waves/drop.helpers";
import { DropVoteState } from "@/hooks/drops/types";
import { useDropInteractionRules } from "@/hooks/drops/useDropInteractionRules";
import { commonApiPost } from "@/services/api/common-api";
import { invalidateWaveApprovalStatusQueries } from "@/hooks/waves/invalidateWaveApprovalStatusQueries";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import dynamic from "next/dynamic";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
export const VOTE_STATE_ERRORS: Record<DropVoteState, string | null> = {
  [DropVoteState.NOT_LOGGED_IN]: "Connect your wallet to rate",
  [DropVoteState.NO_PROFILE]: "Create a profile to rate",
  [DropVoteState.PROXY]: "Proxy can't rate",
  [DropVoteState.CANT_VOTE]: "You are not eligible to rate",
  [DropVoteState.NO_CREDIT]: "You don't have enough credit to rate",
  [DropVoteState.CAN_VOTE]: null,
  [DropVoteState.IS_WINNER]: null,
  [DropVoteState.VOTING_NOT_STARTED]: "Voting has not started yet",
  [DropVoteState.VOTING_ENDED]: "Voting has ended"
};
const DropListItemRateGiveClap = dynamic(() => import("./clap/DropListItemRateGiveClap"), {
  ssr: false
});
const DEFAULT_DROP_RATE_CATEGORY = "Rep";
const DEBOUNCE_DELAY = 300; // milliseconds

export default function DropListItemRateGiveSubmit(t0) {
  const $ = _c(35);
  const {
    rate,
    drop,
    canVote,
    onSuccessfulRateChange,
    isMobile: t1
  } = t0;
  const isMobile = t1 === undefined ? false : t1;
  const {
    requestAuth,
    setToast
  } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const [mutating, setMutating] = useState(false);
  const [clickCount, setClickCount] = useState(0);
  const timeoutRef = useRef(null);
  const {
    voteState
  } = useDropInteractionRules(drop);
  const {
    applyOptimisticDropUpdate
  } = useMyStream();
  const optimisticRollbackRef = useRef(null);
  let t2;
  if ($[0] !== drop) {
    t2 = async param => await commonApiPost({
      endpoint: `drops/${drop.id}/ratings`,
      body: {
        rating: param.rate,
        category: param.category
      }
    });
    $[0] = drop;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== drop || $[3] !== onSuccessfulRateChange || $[4] !== queryClient) {
    t3 = () => {
      onSuccessfulRateChange();
      invalidateWaveApprovalStatusQueries(queryClient, drop.wave.id);
      optimisticRollbackRef.current = null;
    };
    $[2] = drop;
    $[3] = onSuccessfulRateChange;
    $[4] = queryClient;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== setToast) {
    t4 = error => {
      optimisticRollbackRef.current?.();
      optimisticRollbackRef.current = null;
      setToast({
        message: error as unknown as string,
        type: "error"
      });
    };
    $[6] = setToast;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setMutating(false);
      setClickCount(0);
    };
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t2 || $[10] !== t3 || $[11] !== t4) {
    t6 = {
      mutationFn: t2,
      onSuccess: t3,
      onError: t4,
      onSettled: t5
    };
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const rateChangeMutation = useMutation(t6);
  let t7;
  if ($[13] !== applyOptimisticDropUpdate || $[14] !== canVote || $[15] !== clickCount || $[16] !== drop || $[17] !== mutating || $[18] !== rate || $[19] !== rateChangeMutation || $[20] !== requestAuth || $[21] !== setToast || $[22] !== voteState) {
    t7 = async () => {
      if (!canVote) {
        setToast({
          message: VOTE_STATE_ERRORS[voteState],
          type: "warning"
        });
        return;
      }
      if (!rate || mutating) {
        return;
      }
      setMutating(true);
      const {
        success
      } = await requestAuth();
      if (!success) {
        setMutating(false);
        return;
      }
      const previousRate = drop.context_profile_context?.rating ?? 0;
      const rateIncrement = rate * clickCount;
      const newRate = previousRate + rateIncrement;
      if (applyOptimisticDropUpdate && drop.wave.id) {
        const previousHasRating = previousRate !== 0;
        const newHasRating = newRate !== 0;
        const ratersCountDelta = Number(newHasRating) - Number(previousHasRating);
        const ratingDelta = newRate - previousRate;
        optimisticRollbackRef.current?.();
        const handle = applyOptimisticDropUpdate({
          waveId: drop.wave.id,
          dropId: drop.id,
          update: draft => {
            if (draft.type !== DropSize.FULL) {
              return draft;
            }
            const baseContext = draft.context_profile_context ?? drop.context_profile_context ?? {
              rating: 0,
              min_rating: 0,
              max_rating: 0,
              reaction: null,
              boosted: false,
              bookmarked: false,
              curatable: false,
              curated: false
            };
            draft.context_profile_context = {
              ...baseContext,
              rating: newRate
            };
            const currentRating = typeof draft.rating === "number" ? draft.rating : 0;
            draft.rating = currentRating + ratingDelta;
            if (typeof draft.realtime_rating === "number") {
              draft.realtime_rating = draft.realtime_rating + ratingDelta;
            }
            const currentRaters = typeof draft.raters_count === "number" ? draft.raters_count : 0;
            draft.raters_count = Math.max(0, currentRaters + ratersCountDelta);
            return draft;
          }
        });
        optimisticRollbackRef.current = handle?.rollback ?? null;
      }
      await rateChangeMutation.mutateAsync({
        rate: newRate,
        category: DEFAULT_DROP_RATE_CATEGORY
      });
    };
    $[13] = applyOptimisticDropUpdate;
    $[14] = canVote;
    $[15] = clickCount;
    $[16] = drop;
    $[17] = mutating;
    $[18] = rate;
    $[19] = rateChangeMutation;
    $[20] = requestAuth;
    $[21] = setToast;
    $[22] = voteState;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  const submitRate = t7;
  let t8;
  let t9;
  if ($[24] !== clickCount || $[25] !== mutating || $[26] !== submitRate) {
    t8 = () => {
      if (clickCount > 0 && !mutating) {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => {
          submitRate();
        }, DEBOUNCE_DELAY);
      }
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    };
    t9 = [clickCount, mutating, submitRate];
    $[24] = clickCount;
    $[25] = mutating;
    $[26] = submitRate;
    $[27] = t8;
    $[28] = t9;
  } else {
    t8 = $[27];
    t9 = $[28];
  }
  useEffect(t8, t9);
  let t10;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      setClickCount(_temp);
    };
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  const onRateSubmit = t10;
  let t11;
  if ($[30] !== canVote || $[31] !== isMobile || $[32] !== rate || $[33] !== voteState) {
    t11 = <div><DropListItemRateGiveClap rate={rate} onSubmit={onRateSubmit} voteState={voteState} canVote={canVote} isMobile={isMobile} /></div>;
    $[30] = canVote;
    $[31] = isMobile;
    $[32] = rate;
    $[33] = voteState;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  return t11;
}
function _temp(prevCount) {
  return prevCount + 1;
}